import { create } from 'zustand';
import { Reading, Card, SpreadType, Theme } from '../types';
import { getReadings, saveReading, deleteReading } from '../services/readingsService';
import { useAuthStore } from './authStore';
import { supabase } from '../config/supabase';

const STORAGE_KEY = 'pendingReading';

interface ReadingsState {
  readings: Reading[];
  currentReading: Partial<Reading> | null;
  isLoading: boolean;
  error: string | null;
  fetchReadings: () => Promise<void>;
  setCurrentReading: (reading: Partial<Reading>) => void;
  updateCurrentReading: (updates: Partial<Reading>) => void;
  saveCurrentReading: () => Promise<Reading | null>;
  addCard: (card: Card) => void;
  updateCard: (index: number, card: Partial<Card>) => void;
  clearCurrentReading: () => void;
  deleteReading: (id: string) => Promise<void>;
  storePendingReading: () => void;
  restorePendingReading: () => boolean;
  clearPendingReading: () => void;
}

export const useReadingsStore = create<ReadingsState>((set, get) => ({
  readings: [],
  currentReading: null,
  isLoading: false,
  error: null,

  fetchReadings: async () => {
    const { user, initialized, session } = useAuthStore.getState();
    
    if (!initialized) {
      set({ error: 'Auth not initialized. Please wait...', isLoading: false });
      return;
    }
    
    if (!user?.id || !session) {
      set({ 
        readings: [], 
        isLoading: false,
        error: 'Authentication required. Please sign in.'
      });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      // Verify user has a profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('User profile not found');
      }

      const fetchedReadings = await getReadings();
      
      const validReadings = fetchedReadings
        .filter(reading => 
          reading.id &&
          reading.createdAt &&
          reading.subject &&
          reading.context &&
          reading.spreadType &&
          Array.isArray(reading.cards) &&
          reading.cards.length > 0
        )
        .sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA;
        });

      set({ readings: validReadings, isLoading: false, error: null });
    } catch (error) {
      console.error('Error in fetchReadings:', error);
      
      // Provide more specific error messages based on error type
      let errorMessage = 'An unknown error occurred';
      
      if (error instanceof Error) {
        if (error.message.includes('timed out')) {
          errorMessage = 'Connection timed out. Please check your internet connection and try again.';
        } else if (error.message.includes('expired')) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      set({ 
        error: errorMessage, 
        isLoading: false,
        readings: []
      });
    }
  },

  setCurrentReading: (reading) => {
    set({ currentReading: reading });
  },

  updateCurrentReading: (updates) => {
    set({ currentReading: { ...get().currentReading, ...updates } });
  },

  saveCurrentReading: async () => {
    try {
      // Quick pre-check from store
      const { initialized } = useAuthStore.getState();
      if (!initialized) {
        throw new Error('Auth not initialized. Please wait and try again.');
      }

      // Get the verified user directly from Supabase (source of truth)
      // This avoids race conditions where the store snapshot is stale
      const { data: { user: verifiedUser }, error: userError } = await supabase.auth.getUser();
      if (userError || !verifiedUser?.id) {
        throw new Error('Authentication required. Please sign in.');
      }

      set({ isLoading: true, error: null });

      const reading = get().currentReading;
      if (!reading || !reading.subject || !reading.context || !reading.spreadType) {
        throw new Error('Reading is incomplete');
      }

      // Add user_id to the reading using the verified user
      const readingWithUser = {
        ...reading,
        user_id: verifiedUser.id
      };

      // Log reading data before saving
      console.group('Reading Data Analysis');
      console.log('Total reading size:', JSON.stringify(readingWithUser).length, 'bytes');
      console.log('Reading structure:', {
        subject: readingWithUser.subject?.length || 0,
        context: readingWithUser.context?.length || 0,
        spreadType: readingWithUser.spreadType,
        cardsCount: readingWithUser.cards?.length || 0,
        userInterpretation: readingWithUser.userInterpretation?.length || 0,
        aiInterpretation: readingWithUser.aiInterpretation?.length || 0,
        themesCount: readingWithUser.themes?.length || 0,
        advice: readingWithUser.advice?.length || 0,
        notes: readingWithUser.notes?.length || 0
      });

      // Check for potential data issues
      const potentialIssues = [];
      if (readingWithUser.aiInterpretation?.length > 10000) {
        potentialIssues.push('AI interpretation exceeds 10KB');
      }
      if (readingWithUser.context?.length > 2000) {
        potentialIssues.push('Context exceeds 2KB');
      }
      if (readingWithUser.notes?.length > 5000) {
        potentialIssues.push('Notes exceed 5KB');
      }
      
      if (potentialIssues.length > 0) {
        console.warn('Potential data size issues:', potentialIssues);
      }
      console.groupEnd();

      // Try to save the reading
      console.time('💾 Save reading duration');
      const savedReading = await saveReading(readingWithUser as Omit<Reading, 'id' | 'createdAt'>);
      console.timeEnd('💾 Save reading duration');

      if (!savedReading) {
        throw new Error('Failed to save reading');
      }

      set((state) => ({ 
        readings: [savedReading, ...state.readings],
        currentReading: null,
        isLoading: false,
        error: null
      }));

      get().clearPendingReading();
      console.log('🧹 Cleared pending reading from storage');
      return savedReading;
    } catch (error) {
      console.error('Error in saveCurrentReading:', error);
      // Log browser storage state
      console.group('Browser Storage State');
      console.log('Local Storage usage:', JSON.stringify(localStorage).length, 'bytes');
      console.log('Session Storage usage:', JSON.stringify(sessionStorage).length, 'bytes');
      console.groupEnd();

      set({ 
        error: error instanceof Error ? error.message : 'Failed to save reading', 
        isLoading: false
      });
      return null;
    }
  },

  addCard: (card) => {
    set((state) => ({
      currentReading: {
        ...state.currentReading,
        cards: [...(state.currentReading?.cards || []), card]
      }
    }));
  },

  updateCard: (index, card) => {
    set((state) => {
      const currentCards = state.currentReading?.cards || [];
      const updatedCards = [...currentCards];
      updatedCards[index] = { ...updatedCards[index], ...card };
      
      return {
        currentReading: {
          ...state.currentReading,
          cards: updatedCards
        }
      };
    });
  },

  clearCurrentReading: () => {
    set({ currentReading: null });
  },

  deleteReading: async (id) => {
    const { user, initialized, session } = useAuthStore.getState();
    
    if (!initialized) {
      set({ error: 'Auth not initialized. Please wait...' });
      return;
    }
    
    if (!user?.id || !session) {
      set({ error: 'You must be logged in to delete readings' });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const success = await deleteReading(id);
      if (success) {
        set((state) => ({
          readings: state.readings.filter(reading => reading.id !== id),
          isLoading: false,
          error: null
        }));
      } else {
        throw new Error('Failed to delete reading');
      }
    } catch (error) {
      console.error('Error in deleteReading:', error);
      set({ 
        error: error instanceof Error ? error.message : 'An unknown error occurred', 
        isLoading: false 
      });
    }
  },

  storePendingReading: () => {
    const currentReading = get().currentReading;
    if (currentReading) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(currentReading));
        console.log('📦 Stored pending reading:', {
          size: JSON.stringify(currentReading).length,
          keys: Object.keys(currentReading)
        });
      } catch (error) {
        console.error('Error storing pending reading:', error);
      }
    }
  },

  restorePendingReading: () => {
    try {
      const storedReading = localStorage.getItem(STORAGE_KEY);
      if (storedReading) {
        const reading = JSON.parse(storedReading);
        console.log('📦 Restored pending reading:', {
          size: storedReading.length,
          keys: Object.keys(reading)
        });
        set({ currentReading: reading });
        return true;
      }
    } catch (error) {
      console.error('Error restoring reading:', error);
    }
    return false;
  },

  clearPendingReading: () => {
    localStorage.removeItem(STORAGE_KEY);
  }
}));